import React from 'react';
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/modern/components/ui/dropdown-menu';
import {
  canArchive,
  canDelete,
  canShare,
  canCopyToBoard,
} from '../contextMenuUtils';
import { Resource } from '@/bryntum-lib/models/Resource';
import { toast } from 'sonner';
import { useSwivuBryntumContext } from '@/react/hooks/useSwivuBryntumContext';
import { Switch } from '@/modern/components/ui/switch';
import { cn } from '@/modern/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/modern/components/ui/tooltip';

interface ResourceContextMenuProps {
  record: Resource;
  onClose?: () => void;
  onItemClick?: (item: { name: string; }) => void;
}

const ResourceContextMenu: React.FC<ResourceContextMenuProps> = ({ record, onItemClick }) => {
  const context = useSwivuBryntumContext();
  const { user, selectedBoard, boardManager } = context;

  const isDemoUser = user?.isDemoUser ?? false;

  // Permission checks
  const showArchive = user ? canArchive(user, record) : false;
  const showDelete = user ? canDelete(user, record) : false;
  const showShare = user ? canShare(user, isDemoUser, record) : false;
  const showCopyTo = user ? canCopyToBoard(user, isDemoUser, selectedBoard, boardManager?.boardNameStore) : false;

  // Action handlers
  const handleArchive = async () => {
    onItemClick?.({ name: 'archive' });
  };

  const handleDelete = async () => {
    onItemClick?.({ name: 'delete' });
  };

  const handleShare = () => {
    onItemClick?.({ name: 'share' });
  };

  const handleCopyTo = () => {
    onItemClick?.({ name: 'copyTo' });
  };

  const handleBlackoutToggle = (checked: boolean) => {
    record.hideAgenda = checked;
    toast.success(checked ? 'Resource will not be displayed on external schedules' : 'Resource will be displayed on external schedules');
  };

  return (
    <>
      {/* Share */}
      <DropdownMenuItem onClick={handleShare} disabled={!showShare}>
        <i className="fa-light fa-share mr-2" />
        <span>Share</span>
      </DropdownMenuItem>

      {/* Copy to */}
      <DropdownMenuItem onSelect={(e) => e.preventDefault()} onClick={handleCopyTo} disabled={!showCopyTo}>
        <i className="fa-light fa-copy mr-2" />
        <span>Copy to</span>
      </DropdownMenuItem>

      {/* Separator before Blackout */}
      <DropdownMenuSeparator />

      {/* Blackout Toggle */}
      <div className="px-2 py-1.5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <i
              className={cn(
                "flex-shrink-0 text-sm",
                record?.hideAgenda ? "text-destructive fa-regular fa-lightbulb-slash" : "text-primary fa-light fa-lightbulb-on"
              )}
            />
            <Tooltip>
              <TooltipTrigger asChild>
                <span className={cn(
                  "text-sm font-medium",
                  record?.hideAgenda ? "text-destructive" : ""
                )}>
                  Blackout
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {record?.hideAgenda
                  ? "Don't display this resource on external schedules"
                  : "Display this resource on external schedules"}
              </TooltipContent>
            </Tooltip>
          </div>
          <Switch
            checked={record?.hideAgenda || false}
            onCheckedChange={handleBlackoutToggle}
            className={cn(
              "h-5 w-9",
              record?.hideAgenda && "data-[state=checked]:bg-destructive"
            )}
            thumbClassName="h-4 w-4 data-[state=checked]:translate-x-4"
          />
        </div>
      </div>

      {/* Separator before Archive/Delete */}
      <DropdownMenuSeparator />

      {/* Archive/Unarchive */}
      <DropdownMenuItem onClick={handleArchive} disabled={!showArchive}>
        <i className="fa-light fa-folder-open mr-2" />
        <span>{record?.archived ? 'Unarchive' : 'Archive'}</span>
      </DropdownMenuItem>

      {/* Delete */}
      <DropdownMenuItem onClick={handleDelete} className="text-destructive" disabled={!showDelete}>
        <i className="fa-light fa-trash mr-2" />
        <span>Delete</span>
      </DropdownMenuItem>
    </>
  );
};

export default ResourceContextMenu;